<template>
  <div>
    <div class="pr-heading">
      <h2 v-if="!editPage">Create News</h2>
      <h2 v-else>{{ selectLanguage ? 'Translation' : 'Edit News' }}</h2>
      <button @click="translateLanguage" v-if="editPage && !selectLanguage">Translation</button>
      <button @click="selectLanguage = false" v-if="editPage && selectLanguage">Back</button>
    </div>
    <div class="pr-form-container" v-if="!selectLanguage">
      <form>
        <div class="form-input-div">
          <p class="form-label">Title*</p>
          <input type="text" placeholder="Title" class="form-input" v-model="newsValue.title">
          <span class="error-msg" v-if="showInputError && newsValue.title === ''">Fill the title field</span>
          <span class="error-msg" v-if="isValidTitle">Invalid text</span>
          <span class="error-msg" v-if="titleLengthError && newsValue.title !== ''">Please enter below 255 characters</span>
        </div>
        <div class="form-input-div">
          <p class="form-label">Short Description</p>
          <textarea type="text" placeholder="Short description" class="form-input-textarea" v-model="newsValue.shortDesc" />
          <span class="error-msg" v-if="descriptionLengthError && newsValue.shortDesc !== ''">Please enter below 195 characters</span>
        </div>
        <div class="form-input-div">
          <p class="form-label">Lang</p>
          <select v-model="newsValue.language" :disabled="editPage">
            <option v-for="(data,index) in language" :key="index" :value="data">{{ data }}</option>
          </select>
        </div>
        <div class="form-input-div">
          <p class="form-label">Topics*</p>
          <select v-model="newsValue.topics" :disabled="editPage">
            <option v-for="(value, propertyName, index) in newsTopics" :key="index" :value="value">{{ propertyName }}</option>
          </select>
          <p class="error-msg" v-if="showInputError && newsValue.topics === ''">Please select Topics</p>
        </div>
        <div class="form-input-div">
          <p class="form-label">Tags</p>
          <div class="checkbox-div" v-for="(value, propertyName, index) in dataJson.FacetsTags" :key="index">
            <input class="styled-checkbox" :id="index" type="checkbox" :value="value" v-model="newsValue.selectedTags">
            <label :for="index">{{ propertyName }}</label>
          </div>
          <p class="error-msg" v-if="showInputError && newsValue.selectedTags.length === 0">Please select minimum one Tag</p>
        </div>
        <div class="form-input-div">
          <p class="form-label">Hero Image</p>
          <div class="uploaded-file" v-if="newsValue.heroImagebase != ''">
            <div class="card-image">
              <img :src="heroImageSrc" alt="Hero Image" >
            </div>
            <div class="image-alt">
              <label>{{ newsValue.heroImage.name || newsValue.heroImageFileName }}</label>
              <input v-model="newsValue.heroImageAlt" placeholder="Alt">
              <small>Please set an Alt for your image</small>
            </div>
            <div class="file-action-delete">
              <button class="remove-file" type="button" @click="removeImage('hero')">Delete</button>
            </div>
            <div class="file-action-change">
              <button class="select-file" type="button" @click="uploadImage('hero','update')" v-if="editPage">Change</button>
            </div>
            <input type="file" class="file-input" ref="heroImageUpdate" @change="heroFileUpload($event,'update')" accept="image/*" >
          </div>
          <div v-else>
            <label>Add File</label>
            <button class="select-file" type="button" @click="uploadImage('hero','create')">Select a file</button>
            <input type="file" class="file-input" ref="heroImage" @change="heroFileUpload($event,'create')" accept="image/*" >
            <p class="file-text">One file only, 500 KB limit. Allowed type:jpg, png</p>
          </div>
          <div>
            <p class="error-msg" v-if="heroFileSizeError">Please select the file below 500 KB</p>
            <p class="error-msg" v-if="heroFileTypeError && !heroFileSizeError">Invalid File</p>
          </div>
        </div>
        <div class="form-input-div">
          <p class="form-label">Card Image* <small>(216 &times; 400)</small></p>
          <div class="uploaded-file" v-if="newsValue.cardImagebase != ''">
            <div class="card-image">
              <img :src="cardImageSrc" alt="Card Image" >
            </div>
            <div class="image-alt">
              <label>{{ newsValue.cardImage.name || newsValue.cardImageFileName }}</label>
              <input v-model="newsValue.cardImageAlt" placeholder="Alt">
              <small>Please set an Alt for your image</small>
            </div>
            <div class="file-action-delete">
              <button class="remove-file" type="button" @click="removeImage('card')">Delete</button>
            </div>
            <div class="file-action-change" v-if="editPage">
              <button class="select-file" type="button" @click="uploadImage('card','update')">Change</button>
              <p>( If you change this field it will impact other version of item )</p>
            </div>
            <input type="file" class="file-input" ref="cardImageUpdate" @change="cardFileUpload($event,'update')" accept="image/*" >
          </div>
          <div v-else>
            <label>Add File</label>
            <button class="select-file" type="button" @click="uploadImage('card','create')">Select a file</button>
            <input type="file" class="file-input" ref="cardImage" @change="cardFileUpload($event,'create')" accept="image/*" >
            <p class="file-text">One file only, 500 KB limit. Allowed type:jpg, png</p>
          </div>
          <div>
            <p class="error-msg" v-if="showInputError && newsValue.cardImageFileName === '' && !cardFileSizeError">Please add card image</p>
            <p class="error-msg" v-if="cardFileSizeError">Please select the file below 500 KB</p>
            <p class="error-msg" v-if="cardFileTypeError && !cardFileSizeError">Invalid File</p>
          </div>
        </div>
        <div class="form-input-div">
          <p class="form-label">Body*</p>
          <div v-for="(data,index) in newsValue.content" :key="index">
            <vue-editor v-model="data.text" v-if="data.bodyFormat === 'design'" :editor-toolbar="customToolbar" />
            <textarea class="body-text" v-model="data.text" v-else />
            <div class="body-selection">
              <label>Text format</label>
              <select v-model="data.bodyFormat">
                <option value="design">Design</option>
                <option value="basichtml">Basic HTML</option>
              </select>
              <button class="add-btn" type="button" @click="addBody" v-if="newsValue.content.length === index+1 && index !== 4">+Add Body</button>
              <button class="add-btn" type="button" @click="removeBody(index)" v-else>-Remove Body</button>
            </div>
          </div>
          <span class="error-msg" v-if="showInputError && !bodyEmptyError">Please enter minimum 1 body field</span>
        </div>
        <div class="form-input-div">
          <p class="form-label">SEO*</p>
          <input type="text" placeholder="Title" class="form-input" v-model="newsValue.seoTitle">
          <span class="error-msg" v-if="showInputError && newsValue.seoTitle === ''">Fill the SEO Title field</span>
          <span class="error-msg" v-if="isValidSeoTitle && newsValue.seoTitle !== ''">Invalid text</span>
          <input type="text" placeholder="Description" class="form-input seo-desc" v-model="newsValue.seoDesc">
        </div>
        <div class="submit-btn-div">
          <button class="btn-save" @click="createNews('save')" type="button">Save</button>
          <a :href="previewUrl" target="_blank" class="btn-save" :class="{'disabled':!editPage}">Preview</a>
          <button class="btn-save" type="button" @click="createNews('publish')">Save &amp; Publish</button>
        </div>
      </form>
    </div>
    <div class="pr-form-container translation-page" v-else>
      <div class="content-table-container">
        <table aria-describedby="language list">
          <thead>
            <th id="language">
              Language
            </th>
            <th id="title">
              Title
            </th>
            <th id="action-column"/>
          </thead>
          <tbody v-if="translationResult.ResultData !== null">
            <tr v-for="(data,index) in translationResult.ResultData" :key="index">
              <td>
                {{ data.Language }}
              </td>
              <td>{{ data.ItemName }}</td>
              <td>
                <button class="action--btn edit-btn" @click="editLanguage(data)">Edit</button>
              </td>
            </tr>
            <tr v-if="translationResult !== '' && translationResult.ResultData !== null">
              <td>
                <select v-model="selectedTranslationLang">
                  <option value="">Please select Language</option>
                  <option v-for="(data,index) in translationResult.Languages" :key="index" :value="data">{{ data }}</option>
                </select>
              </td>
              <td>{{ translationResult.ResultData[0].ItemName }}</td>
              <td>
                <button class="action--btn delete-btn" @click="createLanguage" :disabled="selectedTranslationLang === ''">Create</button>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="8" style="text-align:center">No Result found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="alert-modal-container" v-if="showSuccessModal">
      <div class="main-content">
        <div class="alert-message">
          <p>{{ successMessage }}</p>
        </div>
        <div class="alert-modal-btn">
          <button class="btn-ok" @click="openContentPage">Ok</button>
          <a :href="previewUrl" target="_blank" class="btn-ok" v-if="previewUrl !== null">Preview</a>
        </div>
      </div>
    </div>
    <api-loader :is-loading="isLoading" />
    <alert-modal v-if="showAlertModal" @closeModal="closeModal" :delete-message="deleteMessage"/>
    <bo-Notification ref="notification" />
  </div>
</template>
<script>
  import { VueEditor } from 'vue2-editor';
  import ApiLoader from '../ApiLoader/api-loader';
  import AlertModal from '../AlertModal/alert-modal';
  import BoNotification from '../BoNotification/bo-notification';
  import { fetchData } from '@Foundation/services';
  export default {
    name: 'bo-news',
    components: {
      VueEditor,
      ApiLoader,
      AlertModal,
      BoNotification
    },
    props: {
      dataJson: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        isLoading: false,
        customToolbar: [[{ header: ['','1','2','3','4','5','6'] }],['bold', 'italic', 'underline','strike'],[{ align: '' },,{ align: 'center' },{ align: 'right' },{ align: 'justify' }],['blockquote','code-block'], [{ list: 'ordered' }, { list: 'bullet' },{ list: 'check' }],[{ color: [] },{ background: [] }], ['link']],
        language: [],
        newsTopics: null,
        newsValue: {
          title: '',
          language: 'en',
          topics: '',
          heroImage: '',
          heroImageAlt: '',
          heroImagebase: '',
          heroImageFileName: '',
          heroImageFileFormat: '',
          cardImage: '',
          cardImageAlt: '',
          cardImagebase: '',
          cardImageFileName: '',
          cardImageFileFormat: '',
          content: [{
            text: '',
            bodyFormat: 'design',
            isEmpty: false
          }],
          seoTitle: '',
          seoDesc: '',
          shortDesc: '',
          selectedTags: []
        },
        heroImageSrc: '',
        cardImageSrc: '',
        showInputError: false,
        selectLanguage: false,
        isValidTitle: false,
        isValidSeoTitle: false,
        previewUrl: '',
        editItemId: '',
        translationResult: '',
        selectedTranslationLang: '',
        languageCreated: false,
        showSuccessModal: false,
        editPage: false,
        isHeroImageDeleted: false,
        isCardImageDeleted: false,
        successMessage: '',
        showAlertModal: false,
        heroImageUrl: '',
        cardImageUrl: '',
        heroFileSizeError: false,
        cardFileSizeError: false,
        titleLengthError: false,
        descriptionLengthError: false,
        bodyEmptyError: false,
        heroFileTypeError: false,
        cardFileTypeError: false,
        deleteMessage: '',
        deleteImageType: ''
      };
    },
    mounted() {
      this.newsTopics = this.dataJson.Topics;
      this.language = this.dataJson.Languages;
      const urlParams = new URLSearchParams(window.location.search);
      const myParam = urlParams.get('ItemId');
      if (myParam !== null) {
        this.editPage = true;
        this.updateValues();
      }
    },
    methods: {
      createNews(type) {
        this.bodyEmptyError = this.newsValue.content.some(function(e) {
          return e.text !== '';
        });
        if (this.newsValue.title === '' || this.newsValue.seoTitle === '' || this.newsValue.topics === '' || this.newsValue.cardImageFileName === '' || this.newsValue.selectedTags.length === 0 || !this.bodyEmptyError) {
          this.showInputError = true;
        } else if (this.newsValue.title.length > 255) {
          this.titleLengthError = true;
        } else if (this.newsValue.shortDesc.length > 195) {
          this.descriptionLengthError = true;
        } else if (this.newsValue.title.indexOf('>') > -1 ||  this.newsValue.title.indexOf('<') > -1 ||  this.newsValue.title.indexOf('%') > -1) {
          this.isValidTitle = true;
        } else if (this.newsValue.seoTitle.indexOf('>') > -1 ||  this.newsValue.seoTitle.indexOf('<') > -1 ||  this.newsValue.seoTitle.indexOf('%') > -1) {
          this.isValidSeoTitle = true;
        } else {
          this.showInputError = false;
          this.isValidTitle = false;
          this.isValidSeoTitle = false;
          this.titleLengthError = false;
          this.descriptionLengthError = false;
          this.isLoading = true;
          const contentBody = this.newsValue.content.map((data)=>{
            if (data.text !== '') {
              return String(data.text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') + 'endofbody';
            }
          });
          let selectedTags = '';
          this.newsValue.selectedTags.forEach((data,index)=>{
            if (this.newsValue.selectedTags.length <= 1) {
              selectedTags = data;
            } else {
              if (this.newsValue.selectedTags.length - 1 === index) {
                selectedTags = `${selectedTags}${data}`;
              } else {
                selectedTags = `${selectedTags}${data}|`;
              }
            }
          });
          const formData = new FormData();
          formData.append('Language', this.newsValue.language);
          formData.append('Topic', this.newsValue.topics);
          formData.append('Title', this.newsValue.title);
          formData.append('SeoTitle', this.newsValue.seoTitle);
          formData.append('SeoDesc', this.newsValue.seoDesc);
          formData.append('BodyContent', contentBody);
          formData.append('ShortDesc',this.newsValue.shortDesc);
          formData.append('SelectedFacetValue', selectedTags);
          formData.append('ArticleType','news');

          if (this.editPage && !this.isHeroImageDeleted) {
            formData.append('HeroImage', '');
            formData.append('HeroImageAlt', '');
            formData.append('HeroImageFileName', '');
            formData.append('HeroImageFileFormat', '');
            formData.append('HeroImageUrl',this.heroImageUrl);
          } else {
            formData.append('HeroImage', this.newsValue.heroImagebase);
            formData.append('HeroImageAlt', this.newsValue.heroImageAlt);
            formData.append('HeroImageFileName', this.newsValue.heroImageFileName);
            formData.append('HeroImageFileFormat', this.newsValue.heroImageFileFormat);
            formData.append('HeroImageUrl',this.heroImageUrl);
          }

          if (this.editPage && !this.isCardImageDeleted) {
            formData.append('CardImage', '');
            formData.append('CardImageAlt', '');
            formData.append('CardImageFileName', '');
            formData.append('CardImageFileFormat', '');
            formData.append('CardImageUrl', this.cardImageUrl);
          } else {
            formData.append('CardImage', this.newsValue.cardImagebase);
            formData.append('CardImageAlt', this.newsValue.cardImageAlt);
            formData.append('CardImageFileName', this.newsValue.cardImageFileName);
            formData.append('CardImageFileFormat', this.newsValue.cardImageFileFormat);
            formData.append('CardImageUrl', this.cardImageUrl);
          }

          if (this.editPage === true) {
            formData.append('ItemId',this.editItemId);
            if (this.languageCreated === true) {
              if (type === 'publish') {
                formData.append('action', 'addlangpublish');
              } else {
                formData.append('action', 'addlangversion');
              }
            } else {
              if (type === 'publish') {
                formData.append('action', 'updateitempublish');
              } else {
                formData.append('action', 'updateitem');
              }
            }
          } else {
            if (type === 'publish') {
              formData.append('action', 'createitempublish');
            } else {
              formData.append('action', 'createitem');
            }
          }
          fetch('/Content-News-PR/ProcessCreateUpdateArticle', {
            method: 'POST',
            body: formData
          })
            .then(response => response.json())
            .then((response) => {
              if (response.IsAuthenticated) {
                this.showSuccessModal = true;
                this.isLoading = false;
                this.previewUrl = response.CreateUpdateResponse.PreviewUrl;
                this.editItemId = response.CreateUpdateResponse.ItemId;
                this.successMessage = response.ResponseMessage === null ? 'Item Created Successfully' : response.ResponseMessage;
              } else {
                window.location.href = '/content-news-pr/newbologin/';
              }
            })
            .catch(() => {
              this.isLoading = false;
              this.$refs.notification.showNotification(true,'Failed',false);
            });
        }
      },

      uploadImage(type,action) {
        if (action === 'create') {
          if (type === 'hero') {
            this.$refs.heroImage.click();
          } else {
            this.$refs.cardImage.click();
          }
        } else {
          if (type === 'hero') {
            this.$refs.heroImageUpdate.click();
          } else {
            this.$refs.cardImageUpdate.click();
          }
        }
      },

      heroFileUpload(e,action) {
        if (this.uploadFileValidation(e)) {
          if (e.target.files[0].size <= 500000) {
            this.createImage(e.target.files[0],'hero');
            this.newsValue.heroImage = e.target.files[0];
            this.newsValue.heroImageAlt = this.sanitizeFileName(e.target.files[0].name);
            this.newsValue.heroImageFileName = this.sanitizeFileName(e.target.files[0].name);
            this.newsValue.heroImageFileFormat = this.getFileType(e.target.files[0].name);
            this.heroFileSizeError = false;
            this.heroFileTypeError = false;
            if (action === 'update') {
              this.isHeroImageDeleted = true;
              this.heroImageUrl = '';
            }
          } else {
            this.heroFileTypeError = false;
            this.heroFileSizeError = true;
          }
        } else {
          this.heroFileSizeError = false;
          this.heroFileTypeError = true;
        }
      },

      uploadFileType(e) {
        return e.target.files[0].type;
      },
      sanitizeFileName(fileName) {
        const newFileName = fileName.split('.');
        let name = '';
        newFileName.forEach((element,i) => {
          if (newFileName.length - 1 !== i) {
            name += element;
          }
        });
        return name;
      },

      getFileType(fileName) {
        const newFileName = fileName.split('.');
        return newFileName[newFileName.length - 1];
      },

      uploadFileValidation(e) {
        if (this.uploadFileType(e) === 'image/jpg' || this.uploadFileType(e) === 'image/jpeg' || this.uploadFileType(e) === 'image/png' || this.uploadFileType(e) === 'image/svg+xml') {
          return true;
        }
        return false;
      },

      cardFileUpload(e,action) {
        if (this.uploadFileValidation(e)) {
          if (e.target.files[0].size <= 500000) {
            this.createImage(e.target.files[0],'card');
            this.newsValue.cardImage = e.target.files[0];
            this.newsValue.cardImageAlt = this.sanitizeFileName(e.target.files[0].name);
            this.newsValue.cardImageFileName = this.sanitizeFileName(e.target.files[0].name);
            this.newsValue.cardImageFileFormat = this.getFileType(e.target.files[0].name);
            this.cardFileSizeError = false;
            this.cardFileTypeError = false;
            if (action === 'update') {
              this.isCardImageDeleted = true;
              this.cardImageUrl = '';
            }
          } else {
            this.cardFileTypeError = false;
            this.cardFileSizeError = true;
          }
        } else {
          this.cardFileSizeError = false;
          this.cardFileTypeError = true;
        }
      },

      createImage(file,type) {
        const reader = new FileReader();
        if (type === 'hero') {
          reader.onload = (e) => {
            this.heroImageSrc = e.target.result;
            this.newsValue.heroImagebase = this.heroImageSrc.split('base64,')[1];
          };
        } else {
          reader.onload = (e) => {
            this.cardImageSrc = e.target.result;
            this.newsValue.cardImagebase = this.cardImageSrc.split('base64,')[1];
          };
        }
        reader.readAsDataURL(file);
      },

      removeImage(type) {
        if (type === 'hero') {
          if (this.editPage && !this.isHeroImageDeleted) {
            this.deleteImageType = type;
            this.showAlertModal = true;
            this.deleteMessage = 'Are you sure you want to delete this media item version from Media library';
          } else {
            this.deleteImageLocal(type);
          }
        } else {
          if (this.editPage && !this.isCardImageDeleted) {
            this.deleteImageType = type;
            this.showAlertModal = true;
            this.deleteMessage = 'If you delete this from media library it will impact other version of item';
          } else {
            this.deleteImageLocal(type);
          }
        }
      },


      deleteImageLocal(type) {
        if (type === 'hero') {
          this.newsValue.heroImage = '';
          this.newsValue.heroImagebase = '';
          this.heroImageSrc = '';
          this.newsValue.heroImageAlt = '',
          this.newsValue.heroImageFileName = '';
          this.newsValue.heroImageFileFormat = '';
        } else {
          this.newsValue.cardImage = '';
          this.newsValue.cardImagebase = '';
          this.cardImageSrc = '';
          this.newsValue.cardImageAlt = '',
          this.newsValue.cardImageFileName = '';
          this.newsValue.cardImageFileFormat = '';
        }
      },

      deleteImage(type) {
        this.isLoading = true;
        const imgUrl = type === 'hero' ? this.heroImageSrc : this.cardImageSrc;
        fetchData(`/Content-News-PR/DeleteMediaItem?mediaUrl=${imgUrl}&language=${this.dataJson.ItemLanguage}`).then((response)=>{
          if (response === true) {
            this.isLoading = false;
            if (type === 'hero') {
              this.deleteImageLocal(type);
              this.isHeroImageDeleted = true;
              this.heroImageUrl = '';
            } else {
              this.deleteImageLocal(type);
              this.isCardImageDeleted = true;
              this.cardImageUrl = '';
            }
            this.$refs.notification.showNotification(true,'Image Deleted Successfully',true);
          } else {
            this.isLoading = false;
            this.$refs.notification.showNotification(true,'Failed To Delete',false);
          }
        });
      },

      addBody() {
        this.newsValue.content.push({
          text: '',
          bodyFormat: 'design',
          isEmpty: false
        });
      },

      removeBody(index) {
        this.newsValue.content.splice(index,1);
      },

      translateLanguage() {
        this.isLoading = true;
        const urlLanguage = new URLSearchParams(window.location.search);
        const languageType = urlLanguage.get('Language');
        fetchData(`/Content-News-PR/LoadTransaltionOptions?itemId=${this.editItemId}&Language=${languageType}`).then((response)=>{
          if (response.IsAuthenticated) {
            this.translationResult = response;
            this.selectLanguage = true;
            this.isLoading = false;
            this.selectedTranslationLang = '';
          } else {
            this.isLoading = false;
            window.location.href = '/content-news-pr/newbologin/';
          }
        });
      },

      updateValues() {
        this.editItemId = this.dataJson.ItemId;
        this.newsValue.title = this.dataJson.Title;
        this.newsValue.language = this.dataJson.ItemLanguage;
        this.newsValue.topics = this.dataJson.ItemTopic;
        this.newsValue.heroImagebase = this.dataJson.HeroImageUrl;
        this.heroImageSrc = this.dataJson.HeroImageUrl;
        this.heroImageUrl = this.dataJson.HeroImageUrl;
        this.cardImageUrl = this.dataJson.CardImgUrl;
        this.cardImageSrc = this.dataJson.CardImgUrl;
        this.newsValue.heroImageAlt = this.dataJson.HeroImgAlt;
        this.newsValue.cardImagebase = this.dataJson.CardImgUrl;
        this.newsValue.cardImageAlt = this.dataJson.CardImgAlt;
        if (this.dataJson.HeroImageUrl !== null && this.dataJson.HeroImageUrl !== '') {
          let heroName = this.dataJson.HeroImageUrl.split('/');
          heroName = heroName[heroName.length - 1];
          this.newsValue.heroImageFileName = heroName;
          this.newsValue.heroImageFileFormat = heroName.split('.')[1];
        } else {
          this.isHeroImageDeleted = true;
        }
        if (this.dataJson.CardImgUrl !== null && this.dataJson.CardImgUrl !== '') {
          let cardName = this.dataJson.CardImgUrl.split('/');
          cardName = cardName[cardName.length - 1];
          this.newsValue.cardImageFileName = cardName;
          this.newsValue.cardImageFileFormat = cardName.split('.')[1];
        } else {
          this.isCardImageDeleted = true;
        }
        if (this.dataJson.Body.length > 0) {
          this.newsValue.content = this.dataJson.Body.map((data)=>{
            return {
              text: data,
              bodyFormat: 'design',
              isEmpty: false
            };
          });
        } else {
          this.newsValue.content = [{
            text: '',
            bodyFormat: 'design',
            isEmpty: false
          }];
        }
        this.newsValue.seoTitle = this.dataJson.SeoTitle;
        this.newsValue.seoDesc = this.dataJson.SeoDesc;
        this.newsValue.shortDesc = this.dataJson.ShortDesc;
        this.previewUrl = this.dataJson.CreateUpdateResponse.PreviewUrl;
        this.dataJson.SelectedFacets.split('|').forEach((data)=>{
          if (data !== '') {
            this.newsValue.selectedTags.push(data);
          }
        });
      },

      createLanguage() {
        this.selectLanguage = false,
        this.newsValue.language = this.selectedTranslationLang;
        this.languageCreated = true;
      },

      openContentPage() {
        window.location.href = `/Content-News-PR/Create-News/?ItemId=${this.editItemId}&Language=${this.newsValue.language}`;
      },

      editLanguage(data) {
        this.selectLanguage = !this.selectLanguage;
        this.languageCreated = false;
        this.newsValue.language = data.Language;
        const urlQuery = new URLSearchParams(window.location.search);
        const lang = urlQuery.get('Language');
        if (data.Language !== lang) {
          window.location.href = `/Content-News-PR/Create-News/?ItemId=${this.editItemId}&Language=${data.Language}`;
        }
      },

      closeModal(val) {
        if (val === 'ok') {
          this.deleteImage(this.deleteImageType);
          this.showAlertModal = false;
        } else {
          this.showAlertModal = false;
        }
      }
    }
  };
</script>
